import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, Heart, Activity, Pill, Filter, Search, Download, Sparkles, Tag, Smile } from 'lucide-react';
import toast from 'react-hot-toast';

const JournalHistory = () => {
  const [entries, setEntries] = useState([]);
  const [insights, setInsights] = useState(null);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    mood: '',
    startDate: '',
    endDate: '',
    template: '',
    searchQuery: ''
  });
  const [view, setView] = useState('timeline'); // timeline or insights
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchEntries();
  }, [filters]);

  useEffect(() => {
    if (view === 'insights') {
      fetchInsights();
    }
  }, [view]);

  const fetchEntries = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (filters.mood) params.append('mood', filters.mood);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.template) params.append('template', filters.template);
      params.append('limit', '50');

      const response = await fetch(
        `http://localhost:5000/api/journal/entries?${params}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      if (response.ok) {
        setEntries(data.entries);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load journal entries');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInsights = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        'http://localhost:5000/api/journal/insights?days=30',
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      if (response.ok) {
        setInsights(data.insights);
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching insights:', error);
      toast.error('Failed to load insights');
    }
  };

  const searchEntries = async () => {
    if (!filters.searchQuery.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `http://localhost:5000/api/journal/search?q=${encodeURIComponent(filters.searchQuery)}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      if (response.ok) {
        setEntries(data.entries);
        toast.success(`Found ${data.entries.length} entries`);
      }
    } catch (error) {
      console.error('Error searching:', error);
      toast.error('Search failed');
    }
  };

  const exportToPDF = async () => {
    toast.success('Exporting to PDF...', { icon: '📄' });
    // TODO: Implement PDF export
  };

  const getMoodEmoji = (mood) => {
    const emojis = {
      very_happy: '😄',
      happy: '😊',
      neutral: '😐',
      sad: '😢',
      very_sad: '😭',
      anxious: '😰',
      stressed: '😫',
      energetic: '⚡',
      tired: '😴',
      calm: '😌'
    };
    return emojis[mood] || '✨';
  };

  const getMoodColor = (mood) => {
    const colors = {
      very_happy: 'bg-green-100 text-green-700',
      happy: 'bg-green-50 text-green-600',
      neutral: 'bg-gray-100 text-gray-700',
      sad: 'bg-blue-100 text-blue-700',
      very_sad: 'bg-blue-200 text-blue-800',
      anxious: 'bg-yellow-100 text-yellow-700',
      stressed: 'bg-red-100 text-red-700',
      energetic: 'bg-purple-100 text-purple-700',
      tired: 'bg-indigo-100 text-indigo-700',
      calm: 'bg-teal-100 text-teal-700'
    };
    return colors[mood] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                <Calendar className="text-purple-500" />
                Journal History
              </h1>
              <p className="text-gray-600 mt-1">Your health journey insights</p>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setView('timeline')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                  view === 'timeline'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Timeline
              </button>
              <button
                onClick={() => setView('insights')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  view === 'insights'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                Insights
              </button>
              <button
                onClick={exportToPDF}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-all flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search entries..."
                value={filters.searchQuery}
                onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                onKeyPress={(e) => e.key === 'Enter' && searchEntries()}
                className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
              />
              <button
                onClick={searchEntries}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all"
              >
                <Search className="w-5 h-5" />
              </button>
            </div>

            <select
              value={filters.mood}
              onChange={(e) => setFilters({ ...filters, mood: e.target.value })}
              className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
            >
              <option value="">All Moods</option>
              <option value="very_happy">😄 Very Happy</option>
              <option value="happy">😊 Happy</option>
              <option value="neutral">😐 Neutral</option>
              <option value="sad">😢 Sad</option>
              <option value="anxious">😰 Anxious</option>
              <option value="stressed">😫 Stressed</option>
              <option value="energetic">⚡ Energetic</option>
              <option value="tired">😴 Tired</option>
            </select>

            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
            />

            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>

        {/* Timeline View */}
        {view === 'timeline' && (
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-gray-600">Loading entries...</p>
              </div>
            ) : entries.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">No entries yet</h3>
                <p className="text-gray-600 mb-6">Start journaling to see your entries here</p>
                <button
                  onClick={() => window.location.href = '/journal'}
                  className="px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-all"
                >
                  Start Journaling
                </button>
              </div>
            ) : (
              entries.map((entry) => (
                <div
                  key={entry._id}
                  className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">{getMoodEmoji(entry.mood)}</div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-gray-800">
                            {new Date(entry.entryDate).toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                          {entry.moodScore && (
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getMoodColor(entry.mood)}`}>
                              Mood: {entry.moodScore}/10
                            </span>
                          )}
                        </div>
                        
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">
                          {entry.template.replace('_', ' ')}
                        </span>
                      </div>

                      <p className="text-gray-700 mb-4 leading-relaxed">{entry.summary}</p>

                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        {entry.energyLevel && (
                          <div className="flex items-center gap-1">
                            <Activity className="w-4 h-4 text-orange-500" />
                            Energy: {entry.energyLevel}/10
                          </div>
                        )}
                        
                        {entry.symptoms && entry.symptoms.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Heart className="w-4 h-4 text-red-500" />
                            {entry.symptoms.length} symptoms
                          </div>
                        )}
                        
                        {entry.medications && entry.medications.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Pill className="w-4 h-4 text-blue-500" />
                            {entry.medications.length} medications
                          </div>
                        )}
                        
                        {entry.activities && entry.activities.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Activity className="w-4 h-4 text-green-500" />
                            {entry.activities.join(', ')}
                          </div>
                        )}
                      </div>

                      {entry.tags && entry.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {entry.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full flex items-center gap-1"
                            >
                              <Tag className="w-3 h-3" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Insights View */}
        {view === 'insights' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="w-8 h-8 text-purple-500" />
                    <div>
                      <p className="text-3xl font-bold text-gray-800">{stats.totalEntries}</p>
                      <p className="text-sm text-gray-600">Total Entries</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Smile className="w-8 h-8 text-green-500" />
                    <div>
                      <p className="text-3xl font-bold text-gray-800">
                        {stats.averageMoodScore?.toFixed(1) || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-600">Avg Mood</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp className="w-8 h-8 text-blue-500" />
                    <div>
                      <p className="text-2xl font-bold text-gray-800">
                        {getMoodEmoji(stats.mostCommonMood)} {stats.mostCommonMood?.replace('_', ' ')}
                      </p>
                      <p className="text-sm text-gray-600">Common Mood</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Activity className="w-8 h-8 text-orange-500" />
                    <div>
                      <p className="text-xl font-bold text-gray-800">
                        {stats.mostCommonActivities || 'None'}
                      </p>
                      <p className="text-sm text-gray-600">Top Activity</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* AI Insights */}
            {insights && insights.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <Sparkles className="text-purple-500" />
                  AI-Generated Insights
                </h2>
                
                <div className="space-y-4">
                  {insights.map((insight, idx) => (
                    <div
                      key={idx}
                      className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-l-4 border-purple-500 rounded-lg"
                    >
                      <p className="text-gray-800 leading-relaxed">{insight}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!insights && (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <Sparkles className="w-16 h-16 text-purple-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">Not enough data yet</h3>
                <p className="text-gray-600">Keep journaling to unlock AI insights!</p>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default JournalHistory;
